import { can, clientName } from '@clim/client-core';
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ClientForm } from '../components/ClientForm';
import { DossierFieldsEditor, type DossierDraft } from '../components/DossierFields';
import { ErrorBox, Input } from '../components/ui';
import { errorMessage, fieldErrors } from '../lib/errors';
import { useEngine, useReady } from '../lib/session';

/**
 * Création d'un dossier dans l'ordre du cahier des charges (§4) :
 * 1. type, 2. client, 3. détails. Le territoire n'est pas demandé : c'est celui du client (déduit de son code postal
 * à la création) ; le dossier est attribué automatiquement au commercial qui le crée. Le brouillon est enregistré localement,
 * même sans réseau ; il sera soumis au contrôle depuis la fiche dossier.
 */
export function NewDossierPage() {
  const engine = useEngine();
  const { me } = useReady();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const types = engine.list('dossier_type').filter((t) => t.isActive).sort((a, b) => a.sortOrder - b.sortOrder);
  // territoires où l'utilisateur peut créer un dossier : seuls leurs clients sont proposés
  const allowed = new Set(
    engine
      .list('territory')
      .filter((t) => t.isActive && (me.permissions['dossier.create'] === 'all' || me.territoryIds.includes(t.id)))
      .map((t) => t.id),
  );
  const preClient = params.get('client') ? engine.get('client', params.get('client')!) : undefined;
  const [step, setStep] = useState(1);
  const [clientId, setClientId] = useState(preClient?.id ?? '');
  const [newClient, setNewClient] = useState(false);
  const [q, setQ] = useState('');
  const [draft, setDraft] = useState<DossierDraft>({ dossierTypeId: types[0]?.id });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const client = clientId ? engine.get('client', clientId) : undefined;
  // client d'un partenaire : chantier en sous-traitance de pose, sans vente (créé par qui planifie)
  const partner = client?.partnerId ? engine.get('reason', client.partnerId) : undefined;
  const partnerBlocked = !!partner && !can(me, 'dossier.schedule');

  const matches = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (term.length < 2) return [];
    return engine
      .list('client')
      .filter((c) => allowed.has(c.territoryId))
      .filter((c) => [clientName(c), c.phone, c.email, c.city, ...c.extraPhones.map((p) => p.number)].some((v) => v?.toLowerCase().includes(term)))
      .slice(0, 8);
  }, [engine, engine.version, q, allowed]);

  const create = async () => {
    setErrors({});
    try {
      const id = await engine.createDossier({ ...draft, dossierTypeId: draft.dossierTypeId!, clientId, territoryId: client!.territoryId });
      navigate(`/dossiers/${id}`, { replace: true });
    } catch (err) {
      setErrors(fieldErrors(err));
      setError(errorMessage(err));
    }
  };

  return (
    <section>
      <h1>{partner ? 'Nouveau chantier partenaire' : 'Nouveau dossier'}</h1>
      <ol className="stepper">
        <li className={step === 1 ? 'on' : 'done'}>Type</li>
        <li className={step === 2 ? 'on' : step > 2 ? 'done' : ''}>Client</li>
        <li className={step === 3 ? 'on' : ''}>Détails</li>
      </ol>

      {step === 1 && (
        <div className="card form">
          <div className="segmented wrap" role="radiogroup" aria-label="Type de dossier">
            {types.map((t) => (
              <button type="button" key={t.id} className={draft.dossierTypeId === t.id ? 'on' : ''} onClick={() => setDraft({ ...draft, dossierTypeId: t.id })}>
                {t.label}
              </button>
            ))}
          </div>
          <button className="btn primary block" disabled={!draft.dossierTypeId} onClick={() => setStep(clientId ? 3 : 2)}>
            Continuer
          </button>
        </div>
      )}

      {step === 2 &&
        (newClient ? (
          <div className="card">
            <ClientForm
              onSaved={(id) => {
                setClientId(id);
                setNewClient(false);
                setStep(3);
              }}
              onCancel={() => setNewClient(false)}
            />
          </div>
        ) : (
          <div className="form">
            <Input type="search" placeholder="Client existant : nom, téléphone, e-mail…" value={q} onChange={(e) => setQ(e.target.value)} autoFocus />
            <ul className="list">
              {matches.map((c) => (
                <li key={c.id}>
                  <button
                    className="row"
                    onClick={() => {
                      setClientId(c.id);
                      setStep(3);
                    }}
                  >
                    <div className="row-main">
                      <strong>{clientName(c)}</strong>
                      <span className="muted">{[c.phone, c.city].filter(Boolean).join(' · ')}</span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
            {can(me, 'client.create') && (
              <button className="btn primary block" onClick={() => setNewClient(true)}>
                + Nouveau client (particulier ou professionnel)
              </button>
            )}
            <button className="btn" onClick={() => setStep(1)}>
              Retour
            </button>
          </div>
        ))}

      {step === 3 && client && (
        <div className="card form">
          <p>
            Client : <strong>{clientName(client)}</strong>{' '}
            <button className="link" onClick={() => setStep(2)}>
              changer
            </button>
          </p>
          {partner && (
            <p className="notice partner-notice">
              Chantier pour le partenaire <strong>{partner.label}</strong> : pose en sous-traitance, <strong>sans vente</strong>{' '}
              (ni prix, ni signature, ni paiement). Indiquez si c’est en hauteur, ajoutez les photos et la photo du bon du
              partenaire, choisissez la puissance de la climatisation, puis envoyez-le en planification.
            </p>
          )}
          <DossierFieldsEditor value={draft} onChange={setDraft} errors={errors} lockType />
          <ErrorBox message={partnerBlocked ? 'Les chantiers partenaires sont créés par un administrateur ou un superviseur.' : error} />
          <button className="btn primary block" disabled={partnerBlocked} onClick={() => void create()}>
            {partner ? 'Créer le chantier' : 'Enregistrer le brouillon'}
          </button>
          <p className="muted">
            {partner
              ? 'Enregistré sur cet appareil, même sans réseau. Vous l’enverrez en planification depuis la fiche.'
              : 'Le brouillon est enregistré sur cet appareil, même sans réseau. Vous le soumettrez au contrôle depuis la fiche.'}
          </p>
        </div>
      )}
    </section>
  );
}
